import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useTeam } from "~/contexts/TeamProvider";
import { canMember } from "~/helpers/authorizations";
import { getLogoPlaceholderUrl } from "~/helpers/logoPlaceholder";
import { routes } from "~/router";

import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import labels from "../../helpers/labels";
import FormLabel from "../login/formLabel/FormLabel";
import Button from "../mantine/button/Button";
import Input from "../ui/input/Input";
import Modal from "../ui/modal/Modal";
import styles from "./Settings.module.scss";
import UploadImage from "./uploadImage/UploadImage";

const TeamDetails = () => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();
  const navigate = useNavigate();
  const { authorizations: teamAuthorizations } = useTeam();
  const canUpdateTeam = canMember(teamAuthorizations, "update");
  const canLeaveTeam = canMember(teamAuthorizations, "leave");

  const { team } = useTeam();
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

  const { mutate: leaveTeam } = useMutation({
    mutationFn: async () => {
      if (!team) {
        throw new Error("Current team is not available.");
      }

      return await apiClient.teams.leaveDelete(team.id);
    },
    onSuccess: () => {
      enqueueSnackbar("User left team successfully", {
        variant: "success",
      });
      navigate(routes.selectTeam());
    },
    onError: (error) => {
      //@ts-expect-error BE types
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
  });
  const handleOpenLeaveModal = () => {
    setIsLeaveModalOpen(true);
  };

  const handleCloseLeaveModal = () => {
    setIsLeaveModalOpen(false);
  };

  const handleLeaveTeam = async () => {
    leaveTeam();
  };

  const handleDeleteLogo = () => {
    setValue("logo", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    // @ts-expect-error TS2551 non-existing className
    <div className={styles.settingsSectionGroup}>
      <p className={styles.settingsText}>
        Update general information about the team, set preferred configurations
        for various aspects of team management
      </p>
      <div className={styles.settingsTeamForm}>
        <div className={styles.teamFormRow}>
          <FormLabel label="Logo" />
          <p className={styles.settingsText}>
            This image will be used while generating the reports and invoices,
            so consider not to leave it blank. Accepted formats: PNG, GIF, JPG,
            JPEG, WebP (max 5MB)
          </p>
          <div className={styles.settingsUpload}>
            <UploadImage
              currentTeam={team}
              handleDeleteLogo={handleDeleteLogo}
              placeholderImageUrl={getLogoPlaceholderUrl(team)}
            />
          </div>
        </div>
        <div className={styles.teamFormRow}>
          <FormLabel label="Name" />
          <Input
            disabled={!canUpdateTeam}
            errormessage={
              errors.name
                ? errors.name?.message?.toString() ||
                  labels.THIS_FIELD_CANNOT_BE_EMPTY
                : ""
            }
            {...register("name", {
              required: true,
            })}
            placeholder="Enter team name"
          />
        </div>

        <div className={styles.teamFormRow}>
          <FormLabel label="Leave Team" />
          {canLeaveTeam ? (
            <p className={styles.settingsText}>
              Leaving the Support team will result in the loss of access to its
              websites. Access can be restored by the team admin sending you an
              invitation.
            </p>
          ) : (
            <p className={styles.settingsText}>
              You have no permissions to leave the team
            </p>
          )}
          <div className={styles.settingsLeaveTeam}>
            <Button
              variant="danger-outline"
              onClick={handleOpenLeaveModal}
              disabled={!canLeaveTeam}
            >
              Leave Team
            </Button>
          </div>
        </div>
      </div>

      <Modal
        key="leaveTeam"
        isOpen={isLeaveModalOpen}
        closeModal={handleCloseLeaveModal}
        buttonLabel="Leave Team"
        formId="contactDeveloper"
        action={handleLeaveTeam}
        actionButtonColor="danger"
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>
            {/*
             // @ts-expect-error TS2339 non-existing className */}
            <div className={styles.dangerIconWrapper}>
              <Icon iconName="DangerIcon" width="1rem" height="1rem" />
            </div>
            Leave Team
          </h3>
        }
      >
        <p>
          Seems like you are going to leave “Support team”, you will lose access
          to the ream and it`s projects. Access can be renewed if admin sends
          you the invite
        </p>
      </Modal>
    </div>
  );
};

export default TeamDetails;
